import { promiseFetch } from '@accedo/promise-fetch';
import { logger } from '@accedo/logger';
import {
  getTealiumUTCTimestamp,
  getTealiumLocalTimestamp,
} from '##/utils/DateTimeHelpers';
import { getDeviceInformation } from '##/utils/device';
import { StandardCarouselItem } from '##/components/StandardCarousel/types';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';
import { MarqueeItem } from '##/components/HomeMarquee/types';
import { isLoggedIn } from '../../authentication/Authentication';
import { version as AppVersion } from '../../../../package.json';
import { config } from '../../store/store';
import { TealiumErrorCode, TealiumErrorContext } from './TealiumErrorTypes';

export type TealiumValues = Record<string, string | number>;
const SESSION_ID_KEY = 'tealium-session-timestamp';
const ADOBE_ECID_KEY = 'adobe-ecid';
const NONE_FOUND = 'none-found';

type TealiumEvent =
  | 'shows_index_screen'
  | 'news_screen'
  | 'live_tv_screen'
  | 'show_screen'
  | 'settings_screen'
  | 'home_screen';
type NavPath = 'shows' | 'news' | 'live-tv' | 'show' | 'setting' | 'home';

type TealiumAppSection =
  | 'shows'
  | 'news'
  | 'livetv'
  | 'shows'
  | 'setting'
  | 'home';

type AppLaunchEventProps = {
  tealium_event: 'app_launch' | 'app_exit';
  call_type: 'view';
  appSection: 'applaunch';
};

const PathToTealiumEvent: Record<NavPath, TealiumEvent> = {
  shows: 'shows_index_screen',
  news: 'news_screen',
  'live-tv': 'live_tv_screen',
  show: 'show_screen',
  setting: 'settings_screen',
  home: 'home_screen',
};

const TealiumEventToAppSection: Record<TealiumEvent, TealiumAppSection> = {
  shows_index_screen: 'shows',
  news_screen: 'news',
  live_tv_screen: 'livetv',
  show_screen: 'shows',
  settings_screen: 'setting',
  home_screen: 'home',
};

const replaceUnknown = (str = ''): string =>
  str?.toLowerCase?.() === 'unknown' ? '' : str;

const getAppLaunchEventProps = (isLaunch = true): AppLaunchEventProps => ({
  tealium_event: `app_${isLaunch ? 'launch' : 'exit'}`,
  call_type: 'view',
  appSection: 'applaunch',
});

export class Tealium {
  private sessionInactivityTimer: number | null = null;
  private timeToReachInactivityMs = 30 * 60 * 1000;
  private apiBase =
    'https://collect.tealiumiq.com/event?tealium_account=10&tealium_profile=main&tealium_datasource=s8ymo1&'; // fallback, to be overridden

  private hasTriedToRequestAdobeEcid = false;
  private adobeEcidEndpointNoTs =
    'https://dpm.demdex.net/id?d_visid_ver=2&d_rtbd=json&d_ver=2&d_orgid=508F0C3A53DB1A8A0A490D4D%40AdobeOrg';

  init = async (): Promise<void> => {
    const configApiBase = config.$get().app?.account?.tealium?.restApiEndpoint;
    this.apiBase = configApiBase || this.apiBase;
    this.resetSessionTimer();
    await this.sendEvent(getAppLaunchEventProps());
  };

  onExit = (): Promise<void> => this.sendEvent(getAppLaunchEventProps(false));

  onSignIn = (
    sourcePath: string,
    destinationPath: string,
    sourceShowId?: string,
  ): void => {
    const sourcePathBase = this.getPathBase(sourcePath);

    let destinationKey = 'nav';
    let sourceShow = '';
    const navigatingToPlayer = destinationPath.includes('/player');
    if (navigatingToPlayer) {
      const isLive = destinationPath.includes('/live/');
      destinationKey = isLive ? 'live' : 'video';
      // only pass sourceShow when destination key is live or video
      sourceShow = sourceShowId || '';
    }

    const signInData = {
      tealium_event: 'user_signin',
      call_type: 'view',
      appSection: sourcePathBase,
      activateLeadScreen: sourcePathBase,
      activateLeadSource: destinationKey,
      ...(sourceShow ? { activateLeadShow: sourceShow } : {}),
    };
    this.sendEvent(signInData);
  };

  // on genre/channelGroup selector item focus
  onLiveTvCategoryEvent = ({
    channelGroup = '',
    numberOfChannelGroups = 0,
    channelGroupIndex = 0,
  }): void => {
    const sourcePathBase = this.getPathBase(window.location.pathname);

    const liveTvCategoryEventData = {
      tealium_event: 'liveCategory',
      call_type: 'view',
      appSection: sourcePathBase,
      interactionCategory: encodeURIComponent(
        channelGroup?.toLowerCase() || '',
      ),
      categoryTotal: numberOfChannelGroups,
      categoryPosition: channelGroupIndex + 1,
    };

    this.sendEvent(liveTvCategoryEventData);
  };

  onCarouselClickEvent = ({
    carouselTitle = '',
    carouselVerticalIndex = 0,
    carouselType = '',
    carouselNumberOfItems = 0,
    carouselItemIndex = 0,
    carouselInteractionCategory = '',
    ...restData
  }: StandardCarouselItem | LiveCarouselItem | MarqueeItem): void => {
    const { liveShow, name, channelKey } = (restData as LiveCarouselItem) || {};
    const {
      title,
      tvShow,
      tvChannel,
      genre,
      videoTypeLongForm,
      videoTypeShortForm,
    } = (restData as StandardCarouselItem) || {};
    const { ctaId, ctaTitle, primaryButtonLabel } =
      (restData as MarqueeItem) || {};

    const carouselCta = ctaTitle || primaryButtonLabel;

    const sourcePathBase = this.getPathBase(window.location.pathname);
    const showContentFormatType = videoTypeLongForm || videoTypeShortForm;

    const showChannel = name || channelKey || tvChannel || ctaId;

    const carouselClickEventProps = {
      ...(showChannel && {
        showChannel: showChannel.toLowerCase(),
      }),
      ...(carouselInteractionCategory && {
        interactionCategory: encodeURIComponent(
          carouselInteractionCategory.toLowerCase(),
        ),
      }),
      ...(carouselCta && { carouselCta }),
      ...(showContentFormatType && { showContentFormatType }),
      showName: liveShow?.title || tvShow || title || NONE_FOUND,
      showGenre: genre?.toLowerCase() || NONE_FOUND,
      tealium_event: 'carousel_click',
      call_type: 'view',
      appSection: sourcePathBase,
      carouselName: carouselTitle?.toLowerCase(),
      carouselType: carouselType?.toLowerCase(),
      carouselPosition: carouselVerticalIndex + 1,
      cardsTotal: carouselNumberOfItems,
      cardPosition: carouselItemIndex + 1,
    };

    this.sendEvent(carouselClickEventProps);
  };

  onSignOut = (prevPath: string): void => {
    const signOutData = {
      tealium_event: 'user_signout',
      call_type: 'view',
      appSection: this.getPathBase(prevPath),
    };
    this.sendEvent(signOutData);
  };

  onVideoEvent = async (
    eventName: string,
    showInfo: TealiumValues,
  ): Promise<void> => {
    const videoData = {
      tealium_event: eventName,
      call_type: 'link',
      ...showInfo,
    };
    await this.sendEvent(videoData);
  };

  onBrowseEvent = (path: string, additionalOptions?: TealiumValues): void => {
    const pathBase = this.getPathBase(path);
    const tealiumEvent = PathToTealiumEvent[pathBase];
    const appSection = TealiumEventToAppSection[tealiumEvent];
    const browseData = {
      tealium_event: tealiumEvent,
      call_type: 'view',
      appSection,
      ...(additionalOptions || {}),
    };
    this.sendEvent(browseData);
  };

  onErrorEvent = (errorObj: TealiumValues): void => {
    const errorData = {
      tealium_event: 'uap_error',
      call_type: 'view',
      ...errorObj,
    };
    this.sendEvent(errorData);
  };

  onFavouriteMoreShows = (path: string): void => {
    this.sendEvent({
      tealium_event: 'favorite_more_shows',
      call_type: 'view',
      appSection: this.getPathBase(path),
    });
  };

  private onFavourite = (isAdd = true) => (
    path: string,
    additionalOptions?: TealiumValues,
  ): void => {
    this.sendEvent({
      tealium_event: isAdd ? 'favorite_show_add' : 'favorite_show_remove',
      call_type: 'view',
      appSection: this.getPathBase(path),
      ...additionalOptions,
    });
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  onAddFavourite = this.onFavourite();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  onRemoveFavourite = this.onFavourite(false);

  private getPathBase = (path: string): NavPath => {
    const pathParts = path.split('/');
    const mainSection = pathParts[1] || 'home';
    return mainSection.toLowerCase() as NavPath;
  };

  private resetSessionTimer = (): void => {
    if (!localStorage.getItem(SESSION_ID_KEY)) {
      // start session if cleared or nonexistent
      localStorage.setItem(SESSION_ID_KEY, String(Date.now()));
    }
    // reset timer
    if (this.sessionInactivityTimer) {
      clearTimeout(this.sessionInactivityTimer);
    }
    this.sessionInactivityTimer = window.setTimeout(
      this.clearSession,
      this.timeToReachInactivityMs,
    );
  };

  private clearSession = (): void => {
    localStorage.removeItem(SESSION_ID_KEY);
    if (this.sessionInactivityTimer) {
      clearTimeout(this.sessionInactivityTimer);
    }
    this.sessionInactivityTimer = null;
  };

  private objToQueryParamList = (obj: TealiumValues): string =>
    Object.keys(obj)
      .map((key) => `${key}=${obj[key] ?? ''}`)
      .join('&');

  private getDateData = (): TealiumValues => {
    const unixTimestamp = Date.now();
    return {
      tealium_timestamp_local: getTealiumLocalTimestamp(unixTimestamp),
      tealium_timestamp_epoch: Math.round(unixTimestamp / 1000),
      tealium_timestamp_utc: getTealiumUTCTimestamp(unixTimestamp),
    };
  };

  private getMemberData = (): TealiumValues => {
    const { user } = config.$get();
    return isLoggedIn() && user
      ? {
          memberId: user.memberId,
          memberEmailHashed: user.memberEmailHash,
          memberAge: user.age,
          memberGender: user.gender,
          memberPostcode: user.postcode,
          memberFavouriteGenre: user.favoriteGenre,
          n10adId: user['10adId'],
        }
      : {};
  };

  private getDeviceData = async (): Promise<TealiumValues> => {
    const userSessionStart =
      localStorage.getItem('tealium-session-timestamp') ||
      // assume inactivity timer to be reset every event and therefore
      // every time this function runs
      'no-session-timestamp-found';

    let adobeEcid = localStorage.getItem(ADOBE_ECID_KEY);
    if (!adobeEcid) {
      if (!this.hasTriedToRequestAdobeEcid) {
        try {
          // only try once
          this.hasTriedToRequestAdobeEcid = true;

          const adobeResponse = await promiseFetch(
            `${this.adobeEcidEndpointNoTs}&ts=${Date.now()}`,
          ).then((response) => response.data);
          const ecid = adobeResponse.d_mid as string;
          localStorage.setItem(ADOBE_ECID_KEY, ecid);
          adobeEcid = ecid;
        } catch (err) {
          this.onErrorEvent({
            errorCode: TealiumErrorCode.FetchAdobeECID,
            errorType: TealiumErrorContext.API,
            errorDetail: JSON.stringify(err),
            errorSource: this.adobeEcidEndpointNoTs,
            errorMessage: '',
          });
          adobeEcid = 'error-setting-ecid';
          logger.error('Error setting adobe ecid for tealium', err);
        }
      } else {
        adobeEcid = 'error-setting-ecid';
      }
    }

    const {
      deviceId,
      model: modelId,
      platform: boxName,
      manufacturer,
    } = await getDeviceInformation();

    const device = replaceUnknown(manufacturer?.toLowerCase()) || 'none-found';

    return {
      device,
      memberLoginStatus: isLoggedIn() ? 'logged-in' : 'logged-out',
      app_version: AppVersion,
      deviceIdAdobe: adobeEcid,
      deviceId: replaceUnknown(deviceId) || 'none-found',
      deviceManufacturer: device,
      tealium_environment: process.env.APP_ENV === 'prod' ? 'prod' : 'dev',
      deviceModel: replaceUnknown(boxName) || 'none-found',
      deviceModelId: replaceUnknown(modelId) || 'none-found',
      tealium_visitor_id: replaceUnknown(deviceId) || 'none-found',
      tealium_session_id: userSessionStart,
      adOptOut: '0',
    };
  };

  private sendEvent = async (eventData: TealiumValues): Promise<void> => {
    this.resetSessionTimer();
    const dateData = this.getDateData();
    const memberData = this.getMemberData();
    const deviceData = await this.getDeviceData();
    const fullQueryParams = this.objToQueryParamList({
      ...dateData,
      ...deviceData,
      ...memberData,
      ...eventData,
    });
    try {
      await promiseFetch(`${this.apiBase}${fullQueryParams}`);
    } catch (err) {
      logger.error(err);
    }
  };
}
